import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { db, functions } from '../../../firebase-client';
import { getMissingRequiredTagGroups, getPricingTagNames } from '../../../utils/requiredTags';
const updateMyMemberTagsFn = httpsCallable(functions, 'updateMyMemberTags');
function normalizeMemberTagMap(rawTags) {
  if (Array.isArray(rawTags)) {
    return rawTags.reduce((acc, tagName) => {
      const key = typeof tagName === 'string' ? tagName.trim() : '';
      if (key) acc[key] = true;
      return acc;
    }, {});
  }
  if (rawTags && typeof rawTags === 'object') {
    return Object.entries(rawTags).reduce((acc, [tagName, enabled]) => {
      const key = typeof tagName === 'string' ? tagName.trim() : '';
      if (key && Boolean(enabled)) acc[key] = true;
      return acc;
    }, {});
  }
  return {};
}
function getAgeRuleTagNames(rule) {
  if (Array.isArray(rule?.tagNames)) {
    return rule.tagNames.filter(name => typeof name === 'string').map(name => name.trim()).filter(Boolean);
  }
  if (typeof rule?.tagName === 'string' && rule.tagName.trim() !== '') {
    return [rule.tagName.trim()];
  }
  return [];
}
function buildVisibleGroups(allTags = [], pricingTagNames = new Set()) {
  const grouped = {};
  const metaByName = new Map();
  const regularVisibilityByName = new Map();
  const ageRuleVisibleTagNames = new Set();
  for (const tag of allTags) {
    if (!tag || tag.type === 'ageRule') continue;
    const name = typeof tag.name === 'string' ? tag.name.trim() : '';
    if (!name) continue;
    regularVisibilityByName.set(name, {
      visibleToMembers: tag.visibleToMembers !== false
    });
    if (tag.visibleToMembers === false) continue;
    const category = typeof tag.category === 'string' && tag.category.trim() ? tag.category.trim() : 'Uncategorised';
    if (!grouped[category]) {
      grouped[category] = {
        category,
        selfAssignable: false,
        multiAssignable: false,
        requiredForMembers: false,
        isPricingGroup: false,
        tags: []
      };
    }
    grouped[category].selfAssignable = grouped[category].selfAssignable || Boolean(tag.selfAssignable);
    grouped[category].requiredForMembers = grouped[category].requiredForMembers || tag.requiredForMembers === true;
    grouped[category].isPricingGroup = grouped[category].isPricingGroup || pricingTagNames.has(name);

    // Only expose self-assignable tags in the editable picker.
    if (tag.selfAssignable && !pricingTagNames.has(name) && !grouped[category].tags.includes(name)) {
      grouped[category].tags.push(name);
    }
    if (tag.selfAssignable) {
      grouped[category].multiAssignable = grouped[category].multiAssignable || Boolean(tag.multiAssignable);
    }
    metaByName.set(name, {
      category,
      selfAssignable: Boolean(tag.selfAssignable),
      visibleToMembers: tag.visibleToMembers !== false,
      systemManaged: tag.systemManaged === true,
      requiredForMembers: tag.requiredForMembers === true,
      isPricingTag: pricingTagNames.has(name)
    });
  }
  for (const tag of allTags) {
    if (!tag || tag.type !== 'ageRule') continue;
    if (tag.visibleToMembers === false) continue;
    const ruleTagNames = getAgeRuleTagNames(tag);
    ruleTagNames.forEach(name => {
      const regularMeta = regularVisibilityByName.get(name);
      if (regularMeta && regularMeta.visibleToMembers === false) return;
      ageRuleVisibleTagNames.add(name);
    });
  }
  const groups = Object.values(grouped).sort((a, b) => a.category.localeCompare(b.category));
  groups.forEach(group => group.tags.sort((a, b) => a.localeCompare(b)));
  return {
    groups,
    metaByName,
    ageRuleVisibleTagNames
  };
}
export default function MyTagsCard({
  teamId,
  allTags = [],
  memberTags = {}
}) {
  const [stripeSetup, setStripeSetup] = useState(null);
  const [selectedByCategory, setSelectedByCategory] = useState({});
  const [savedSnapshot, setSavedSnapshot] = useState('');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const memberTagMap = useMemo(() => normalizeMemberTagMap(memberTags), [memberTags]);
  useEffect(() => {
    let cancelled = false;
    if (!teamId) {
      setStripeSetup(null);
      return undefined;
    }
    Promise.resolve(getDoc(doc(db, 'teams', teamId, 'stripe', 'setup')))
      .then(snap => {
        if (!cancelled) setStripeSetup(snap?.exists?.() ? snap.data() || {} : {});
      })
      .catch(() => {
        if (!cancelled) setStripeSetup({});
      });
    return () => {
      cancelled = true;
    };
  }, [teamId]);
  const pricingTagNames = useMemo(() => getPricingTagNames(stripeSetup || {}), [stripeSetup]);
  const assignedTagNames = useMemo(() => Object.keys(memberTagMap).filter(name => memberTagMap[name]).sort((a, b) => a.localeCompare(b)), [memberTagMap]);
  const {
    groups: visibleGroups,
    metaByName,
    ageRuleVisibleTagNames
  } = useMemo(() => buildVisibleGroups(allTags, pricingTagNames), [allTags, pricingTagNames]);
  const lockedAssignedTagNames = useMemo(() => assignedTagNames.filter(tagName => {
    const meta = metaByName.get(tagName);
    if (!meta) return ageRuleVisibleTagNames.has(tagName);
    if (meta.systemManaged || meta.visibleToMembers === false) return false;
    return !meta.selfAssignable || meta.isPricingTag;
  }), [assignedTagNames, metaByName, ageRuleVisibleTagNames]);
  const editableGroups = useMemo(() => visibleGroups.filter(group => group.selfAssignable && !group.isPricingGroup), [visibleGroups]);
  const lockedPricingGroups = useMemo(() => visibleGroups.filter(group => group.isPricingGroup), [visibleGroups]);
  const requiredEditableGroups = useMemo(() => editableGroups.filter(group => group.requiredForMembers), [editableGroups]);
  useEffect(() => {
    const next = {};
    const assignedSet = new Set(assignedTagNames);
    editableGroups.forEach(group => {
      next[group.category] = group.tags.filter(tagName => assignedSet.has(tagName));
    });
    const snapshot = JSON.stringify(next);
    setSelectedByCategory(next);
    setSavedSnapshot(snapshot);
    setStatus('');
    setError('');
  }, [editableGroups, assignedTagNames]);
  const isDirty = JSON.stringify(selectedByCategory) !== savedSnapshot;
  const toggleTag = (category, tagName, multiAssignable) => {
    setStatus('');
    setError('');
    setSelectedByCategory(prev => {
      const existing = Array.isArray(prev[category]) ? prev[category] : [];
      const isSelected = existing.includes(tagName);
      const nextForCategory = multiAssignable ? isSelected ? existing.filter(name => name !== tagName) : [...existing, tagName] : isSelected ? [] : [tagName];
      return {
        ...prev,
        [category]: nextForCategory
      };
    });
  };
  const handleSave = async () => {
    if (!teamId || saving || !isDirty) return;
    setSaving(true);
    setStatus('');
    setError('');
    try {
      const selectedTags = Object.values(selectedByCategory).flat().filter(Boolean);
      const missingRequired = getMissingRequiredTagGroups(requiredEditableGroups, selectedTags, {
        includePricing: false
      });
      if (missingRequired.length > 0) {
        setError(`Choose a tag in ${missingRequired[0].category} before saving.`);
        setSaving(false);
        return;
      }
      await updateMyMemberTagsFn({
        teamId,
        selectedTags
      });
      setSavedSnapshot(JSON.stringify(selectedByCategory));
      setStatus('Tags updated.');
    } catch (err) {
      setError(err?.message || 'Failed to update tags.');
    } finally {
      setSaving(false);
    }
  };
  return <div className="team-card">
      <div className="card-header-with-icon">
        <span className="card-title">My Tags</span>
      </div>
      <div className="card-body">
        <p className="text-muted" style={{
        marginBottom: '0.75rem'
      }}>
          Locked tags are assigned automatically or managed through membership changes. Selectable tags can be updated below.
        </p>

        {assignedTagNames.length === 0 ? <p className="text-muted">No tags assigned yet.</p> : lockedAssignedTagNames.length > 0 ? <div className="tags-list" style={{
        padding: 0,
        marginBottom: '1rem'
      }}>
            {lockedAssignedTagNames.map(tagName => <span key={tagName} className="tag-chip">
                {tagName} {'🔒'}
              </span>)}
          </div> : null}

        {lockedPricingGroups.length > 0 && <div style={{
          marginBottom: '1rem'
        }}>
                {lockedPricingGroups.map(group => {
            const selected = group.tags.filter(tagName => assignedTagNames.includes(tagName));
            return <div key={`pricing-${group.category}`} className="tag-category" style={{
              marginBottom: '0.75rem'
            }}>
                    <strong>{group.category}{group.requiredForMembers ? ' (required)' : ''}</strong>
                    <p className="text-muted" style={{
                marginTop: '0.25rem',
                marginBottom: '0.5rem'
              }}>
                      Change pricing-affecting tags from Manage Membership.
                    </p>
                    {selected.length > 0 ? <div className="tags-list" style={{
                padding: 0
              }}>
                        {selected.map(tagName => <span key={tagName} className="tag-chip">{tagName}</span>)}
                      </div> : <p className="text-muted">No pricing tag selected.</p>}
                  </div>;
          })}
              </div>}

        {editableGroups.length === 0 ? <p className="text-muted">No self-assignable tags are available.</p> : <>
            {editableGroups.map(group => <fieldset key={group.category} className="tag-category" style={{
          marginBottom: '0.75rem'
        }}>
                <legend>
                  {group.category}
                  {!group.multiAssignable ? ' (choose one)' : ''}
                  {group.requiredForMembers ? ' (required)' : ''}
                </legend>
                <div className="tag-options" style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem'
          }}>
                  {group.tags.map(tagName => {
              const selected = selectedByCategory[group.category]?.includes(tagName);
              return <label key={`${group.category}:${tagName}`} className={`tag-pill ${selected ? 'selected' : ''}`}>
                        <input type="checkbox" checked={Boolean(selected)} onChange={() => toggleTag(group.category, tagName, group.multiAssignable)} />
                        {tagName}
                      </label>;
            })}
                </div>
              </fieldset>)}

            <div style={{
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'center'
        }}>
              <button type="button" className="btn btn--primary btn--sm" disabled={!isDirty || saving || stripeSetup === null} onClick={handleSave}>
                {saving ? 'Saving...' : 'Save My Tags'}
              </button>
              {status && <span className="text-muted">{status}</span>}
            </div>
            {error && <p className="text-muted" style={{
          marginTop: '0.5rem'
        }}>{error}</p>}
          </>}
      </div>
    </div>;
}
MyTagsCard.propTypes = {
  teamId: PropTypes.string.isRequired,
  allTags: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.string,
    name: PropTypes.string,
    category: PropTypes.string,
    selfAssignable: PropTypes.bool,
    multiAssignable: PropTypes.bool,
    visibleToMembers: PropTypes.bool,
    requiredForMembers: PropTypes.bool
  })),
  memberTags: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.object])
};
